<template>
  <span class="metric-info">
    <button
      type="button"
      class="metric-info-btn"
      :id="triggerId"
      :aria-label="`What does ${entry.label} mean?`"
      @click.prevent.stop="openBreakdown"
    >
      <i class="la la-info-circle" />
    </button>

    <b-popover
      :target="triggerId"
      :placement="placement"
      triggers="hover focus"
      boundary="viewport"
      custom-class="metric-info-popover"
    >
      <template #title>{{ entry.label }}</template>

      <p class="mi-short">{{ entry.short }}</p>

      <p class="mi-formula">
        <span class="mi-formula-label">How it's calculated</span>
        {{ entry.formula }}
      </p>

      <p v-if="entry.gotcha" class="mi-gotcha">{{ entry.gotcha }}</p>

      <p class="mi-hint">Click the icon for the full breakdown of every number on this page.</p>
    </b-popover>
  </span>
</template>

<script>
/* eslint-disable */

/**
 * A small info icon that explains one number.
 *
 * Hover or tab to it for the short answer; click it to open the page's full breakdown.
 * The click deliberately does NOT put links inside the popover — a hover-triggered popover
 * closes as soon as the pointer leaves the icon, so anything clickable inside it would be
 * unreachable.
 *
 * Deliberately generic (it renders whatever entry it is handed) so other pages with the same
 * "what does this number mean?" problem can reuse it. Huddle's copy lives in
 * src/pages/Huddle/huddleMetricGlossary.js.
 */
export default {
  name: 'MetricInfo',

  props: {
    // A glossary entry: { key, label, short, formula, gotcha, docsAnchor }
    entry: {
      type: Object,
      required: true,
    },
    placement: {
      type: String,
      default: 'top',
    },
  },

  computed: {
    /**
     * Several of these render in a single table row, so the target id has to be unique per
     * instance. _uid is Vue's own per-instance counter — stable for the life of the component,
     * unlike a Math.random() computed, which returns a new value on every re-render.
     */
    triggerId() {
      return `metric-info-${this._uid}`;
    },
  },

  methods: {
    openBreakdown() {
      // The popover is still open under the pointer — close it so it doesn't sit on the modal.
      this.$root.$emit('bv::hide::popover', this.triggerId);
      this.$emit('open-breakdown', this.entry.key);
    },
  },
};
</script>

<style lang="scss" scoped>
.metric-info {
  display: inline-flex;
  vertical-align: middle;
}

.metric-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-left: 4px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover,
  &:focus {
    color: #3abf94;
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #3abf94;
    outline-offset: 2px;
    border-radius: 50%;
  }

  i {
    display: block;
  }
}
</style>

<!-- Unscoped: b-popover renders outside this component, so the scope attribute never reaches it.
     Contained by the custom-class, same approach as FeatureGuide.vue's .fg-help-popover. -->
<style lang="scss">
.metric-info-popover {
  max-width: 320px;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;

  .popover-header {
    background: #f8fafc !important;
    color: #0f172a !important;
    font-size: 13px;
    font-weight: 600;
    border-bottom: 1px solid #e2e8f0 !important;
    padding: 10px 14px !important;
  }

  .popover-body {
    padding: 12px 14px !important;
    font-size: 12.5px;
    line-height: 1.55;
    color: #334155;
  }

  .mi-short {
    margin-bottom: 10px;
  }

  .mi-formula {
    margin-bottom: 10px;
    padding: 8px 10px;
    background: #f1f5f9;
    border-radius: 6px;
    color: #0f172a;
  }

  .mi-formula-label {
    display: block;
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    margin-bottom: 2px;
  }

  .mi-gotcha {
    margin-bottom: 10px;
    padding: 8px 10px;
    background: #fffbeb;
    border-left: 3px solid #f59e0b;
    border-radius: 0 6px 6px 0;
    color: #b45309;
  }

  .mi-hint {
    margin-bottom: 0;
    padding-top: 8px;
    border-top: 1px solid #e2e8f0;
    font-size: 11.5px;
    color: #64748b;
  }
}
</style>
